/**
 * AvailabilitySeoMeta.js
 *
 * SEO + structured data component for the class-schedule / availability page.
 * Mirrors WelcomeSeoMeta.js's patterns: keyed <Helmet> so meta always flushes
 * on change, a word-boundary-safe description trimmer, and JSON-LD structured
 * data appropriate to the page (WebPage + BreadcrumbList, same shape as
 * WelcomeSeoMeta — this page isn't itself a purchasable "Offer" the way the
 * diet planner's WebApplication schema was).
 */

import React from "react";
import { Helmet } from "react-helmet-async";

// ─── Constants ─────────────────────────────────────────────────────────────────
const SITE_NAME      = "Yoga Shastra";
const SITE_URL       = "https://www.yoga-shastra.com";
const OG_IMAGE        = `${SITE_URL}/og-image-availability.jpg`;
const TWITTER_HANDLE  = "@yogashastra";
const PAGE_PATH       = "/availability";

// ─── Safe description trimmer — respects word boundaries ──────────────────────
const trimDesc = (str = "", max = 155) => {
  if (!str || str.length <= max) return str;
  return str.slice(0, max).replace(/\s+\S*$/, "").trim() + "…";
};

// ═══════════════════════════════════════════════════════════════════════════════
// AvailabilityPageMeta
// ═══════════════════════════════════════════════════════════════════════════════

export const AvailabilityPageMeta = ({
  title: titleOverride,
  description: descriptionOverride,
} = {}) => {
  const title = titleOverride
    ? `${titleOverride} – ${SITE_NAME}`
    : `Class Schedule & Availability | ${SITE_NAME}`;

  const description = trimDesc(
    descriptionOverride ||
      "Choose a convenient date and time for your Yoga Shastra session — free to check, takes less than a minute, no payment required."
  );

  const canonical = `${SITE_URL}${PAGE_PATH}`;

  // ── BreadcrumbList JSON-LD ───────────────────────────────────────────────────
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "How It Works", item: `${SITE_URL}/how-it-works` },
      { "@type": "ListItem", position: 3, name: "Availability", item: canonical },
    ],
  };

  // Keyed so Helmet reliably flushes if title/description ever change client-side
  return (
    <Helmet key={canonical}>
      {/* ── Primary meta ── */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* ── Open Graph ── */}
      <meta property="og:type"         content="website" />
      <meta property="og:site_name"    content={SITE_NAME} />
      <meta property="og:title"        content={title} />
      <meta property="og:description"  content={description} />
      <meta property="og:url"          content={canonical} />
      <meta property="og:image"        content={OG_IMAGE} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale"       content="en_IN" />

      {/* ── Twitter card ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content={TWITTER_HANDLE} />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={OG_IMAGE} />

      {/* ── Structured data ── */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};